import axios from "axios";

// Bot detection for prerendered crawler HTML — skip heavy interactive widgets (maps) for bots
// so the prerender layer renders fast and slim (crawler-cache TTFB stays low for Google/AI bots).
export const IS_BOT = typeof navigator !== "undefined"
  && /bot|crawler|spider|gptbot|claude|bingbot|slurp|duckduck|facebookexternal/i.test(navigator.userAgent);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
// Canonical production site URL (used for SEO canonical / OG tags).
export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://zateplenia.eu").replace(/\/$/, "");

// Contact & company (RESTOB s.r.o.)
export const PHONE_DISPLAY = "0907 649 964";
export const PHONE_TEL = "+421907649964";
export const SOCIALS = [];
export const COMPANY = {
  name: "RESTOB s.r.o.",
  street: "Kopčianska 10",
  city: "851 01 Bratislava",
  ico: "50538381",
  dic: "2120374344",
  icdph: "SK2120374344",
  court: "Mestský súd Bratislava III",
  section: "Sro",
  insert: "114669/B",
  email: "info@zateplenia.eu",
};

// ---- Cookie consent (analytics gating) ----
export const getCookieConsent = () => localStorage.getItem("z_cookie_consent") || ""; // "" | "accepted" | "rejected"
export const setCookieConsent = (v) => {
  localStorage.setItem("z_cookie_consent", v);
  window.dispatchEvent(new CustomEvent("z-cookie-consent", { detail: v }));
};
export const hasAnalyticsConsent = () => getCookieConsent() === "accepted";
export const resetCookieConsent = () => {
  localStorage.removeItem("z_cookie_consent");
  window.dispatchEvent(new CustomEvent("z-cookie-consent", { detail: "" }));
};

export const api = axios.create({ baseURL: API });

// ---- Auth (JWT Bearer) ----
export const getToken = () => localStorage.getItem("z_token") || "";
export const setToken = (t) => localStorage.setItem("z_token", t);
export const clearToken = () => localStorage.removeItem("z_token");

export const authApi = () =>
  axios.create({ baseURL: API, headers: { Authorization: `Bearer ${getToken()}` } });

// backwards-compatible helper (ignores arg, uses stored token)
export const adminApi = () => authApi();

// ---- Image upload (Emergent object storage) ----
export const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post(`${API}/upload`, fd, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  return data.url; // e.g. "/api/files/..."
};

// Resolve a stored image path to a full URL (absolute URLs pass through).
export const imgUrl = (u) => (!u ? "" : /^https?:\/\//.test(u) ? u : `${BACKEND_URL}${u}`);

// ---- Analytics ----
export const getVisitor = () => {
  let id = localStorage.getItem("z_vid");
  const returning = !!id;
  if (!id) {
    id = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem("z_vid", id);
  }
  return { id, returning };
};

export const track = (event = "visit", label = "", path = window.location.pathname, value = null) => {
  // Only send analytics after the visitor has accepted cookies (GDPR/ePrivacy).
  if (localStorage.getItem("z_cookie_consent") !== "accepted") return;
  const { id, returning } = getVisitor();
  api.post("/track", {
    event, label, path, value,
    referrer: document.referrer || "",
    visitor_id: id, returning,
  }).catch(() => {});
};

export const scrollToId = (id) => {
  const el = document.querySelector(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
};

// ---- Contact channels (editable) ----
export const CONTACT = {
  phone: "+421 900 123 456",
  phoneHref: "tel:+421900123456",
  whatsapp: "https://wa.me/421900123456",
  email: "info@zateplenia.eu",
  emailHref: "mailto:info@zateplenia.eu",
};

// ---- Funnels (emotional / professional / strategic) ----
export const FUNNELS = [
  {
    slug: "strach",
    kicker: "Nerobte drahú chybu",
    title: "Zle zateplený dom vás bude stáť tisíce",
    subtitle: "Vlhké steny, pleseň, uniknuté teplo a účty, ktoré rastú každú zimu. Kým váhate, peniaze doslova unikajú cez steny.",
    tone: "fear",
    bullets: [
      "Každá zima bez zateplenia = stovky € preč cez fasádu",
      "Zlý materiál či partner = popraskaná omietka o 2 roky",
      "Pleseň a vlhkosť ohrozujú zdravie vašej rodiny",
    ],
    cta: "Zistiť, koľko strácam",
  },
  {
    slug: "istota",
    kicker: "Overená kvalita",
    title: "Zateplenie, na ktoré sa spoľahnete",
    subtitle: "Viac ako 350 000 m² zateplených fasád, overený partner vo vašom okrese a záruka 36/60 mesiacov. Profesionálne, transparentne, bez prekvapení.",
    tone: "trust",
    bullets: [
      "Overený realizátor priamo vo vašom okrese",
      "Transparentná cena — materiál, práca, lešenie aj doprava",
      "Záruka 36 mesiacov na prácu a 60 mesiacov na materiál",
    ],
    cta: "Chcem istotu a cenu",
  },
  {
    slug: "uspora",
    kicker: "Strategická investícia",
    title: "Zateplite teraz, šetrite ďalších 30 rokov",
    subtitle: "Správne zateplenie zníži účty za energie až o 40 % a zhodnotí nehnuteľnosť. Navyše viete využiť dotáciu Obnov dom.",
    tone: "strategy",
    bullets: [
      "Úspora na energiách až 40 % ročne",
      "Vyššia hodnota nehnuteľnosti pri predaji",
      "Možnosť dotácie Obnov dom až do výšky tisícov €",
    ],
    cta: "Vypočítať moju úsporu",
  },
];

export const funnelBySlug = (slug) => FUNNELS.find((f) => f.slug === slug);

export const BUILDING_TYPES = [
  { key: "family_house", label: "Rodinný dom", desc: "Novostavba alebo staršia budova." },
  { key: "apartment_building", label: "Bytový dom", desc: "Panelák alebo bytovka." },
  { key: "building", label: "Budova / objekt", desc: "Administratíva, škola, prevádzka." },
];

export const REALIZATION = [
  { key: "asap", label: "Čo najskôr", desc: "Expresný termín" },
  { key: "3months", label: "Do 3 mesiacov", desc: "Blízka sezóna" },
  { key: "thisyear", label: "Tento rok", desc: "Plánujem tento rok" },
  { key: "flexible", label: "Flexibilne", desc: "Zatiaľ zisťujem cenu" },
];
